// Admin → Signature Purge
//
// One-time cleanup tool for the early-May 2026 PreCon forms where
// signature images accidentally ended up inside the `scouting_photos[]`
// array. The scan endpoint auto-discovers every PreCon in a date
// window (default May 1–15, 2026) with at least one signature-shaped
// entry; the admin confirms visually, types PURGE, and the execute
// endpoint removes the selected entries via a surgical MongoDB $pull
// — no form-PUT, no status touch, no Cloudinary file delete.
//
// Calls:
//   GET  /api/admin/signature-purge/scan?start_date=...&end_date=...
//   POST /api/admin/signature-purge/execute   { confirm_token, removals }
//
// Admin-only — calls fail at the backend if the user isn't admin.

import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  AlertTriangle, CheckCircle2, RefreshCw, Camera, Search, ShieldAlert, ImageOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { SafeImage } from '../components/SafeImage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CONFIRM_TOKEN = 'PURGE_SIGS';
const CONFIRM_WORD  = 'PURGE';

const HEURISTIC_LABEL = {
  url_matches_submitter_signature: 'URL = submitter signature',
  url_matches_reviewer_signature:  'URL = reviewer signature',
  data_uri:                        'Base64 data URI',
  public_id_contains_signature:    'public_id contains "signature"',
  signatures_folder_path:          'URL is in /signatures/ folder',
};

export default function AdminSignaturePurge() {
  const [startDate, setStartDate] = useState('20260501');
  const [endDate,   setEndDate]   = useState('20260515');
  const [scanning, setScanning] = useState(false);
  const [report,   setReport]   = useState(null);
  // Map: `${form_id}:${photo_id}` -> bool (checked for removal)
  const [selected, setSelected] = useState({});
  const [confirmText, setConfirmText] = useState('');
  const [alsoDeleteRemote, setAlsoDeleteRemote] = useState(true);  // default checked per spec
  const [executing,   setExecuting]   = useState(false);
  const [executeResult, setExecuteResult] = useState(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('fieldflow_token')}` },
  });

  const runScan = async () => {
    setScanning(true);
    setReport(null);
    setSelected({});
    setExecuteResult(null);
    setConfirmText('');
    try {
      const r = await axios.get(
        `${API}/admin/signature-purge/scan`,
        { ...authHeaders(), params: { start_date: startDate, end_date: endDate } }
      );
      setReport(r.data);
      // Pre-check every suspect — admin can uncheck false positives.
      const sel = {};
      for (const f of (r.data.forms || [])) {
        for (const p of (f.photos || [])) {
          if (p.looks_like_signature) sel[`${f.form_id}:${p.photo_id}`] = true;
        }
      }
      setSelected(sel);
      const t = r.data.totals;
      if (t.total_suspects === 0) {
        toast.success(`Scanned ${t.forms_examined} PreCons — no signature-shaped entries found.`);
      } else {
        toast.warning(
          `Found ${t.total_suspects} signature-shaped entries across ${t.forms_with_suspects} form(s).`
        );
      }
    } catch (e) {
      toast.error(`Scan failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setScanning(false);
    }
  };

  const toggle = (formId, photoId) => {
    const k = `${formId}:${photoId}`;
    setSelected((s) => ({ ...s, [k]: !s[k] }));
  };

  const selectionList = useMemo(() => {
    if (!report) return [];
    const out = [];
    for (const f of (report.forms || [])) {
      for (const p of (f.photos || [])) {
        const k = `${f.form_id}:${p.photo_id}`;
        if (selected[k]) {
          out.push({
            form_id: f.form_id,
            job_number: f.job_number,
            photo_id: p.photo_id,
            heuristic: p.matched_heuristic,
            url: p.url,
          });
        }
      }
    }
    return out;
  }, [report, selected]);

  const canExecute = confirmText.trim() === CONFIRM_WORD && selectionList.length > 0 && !executing;

  const executePurge = async () => {
    if (!canExecute) return;
    setExecuting(true);
    setExecuteResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/signature-purge/execute`,
        {
          confirm_token: CONFIRM_TOKEN,
          also_delete_remote_file: alsoDeleteRemote,
          removals: selectionList.map(({ form_id, photo_id }) => ({ form_id, photo_id })),
        },
        authHeaders(),
      );
      setExecuteResult(r.data);
      const t = r.data.totals;
      toast.success(`Removed ${t.removed} of ${t.requested}. Skipped ${t.skipped}.`);
      // Re-scan to show the now-clean state.
      await runScan();
    } catch (e) {
      toast.error(`Execute failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setExecuting(false);
      setConfirmText('');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-signature-purge-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldAlert className="w-7 h-7 text-amber-600" />
          Signature Purge — PreCon scouting_photos cleanup
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">
          Auto-discovers PreCon forms in the chosen date window whose
          <code className="mx-1 px-1 bg-slate-100 rounded">scouting_photos[]</code>
          array contains entries that look like signatures (data URIs,
          Cloudinary <code className="px-1 bg-slate-100 rounded">/signatures/</code>{' '}
          paths, or URLs matching the form&apos;s own signature). Surgical
          MongoDB <code className="px-1 bg-slate-100 rounded">$pull</code>{' '}
          on confirm — does not touch approval status, signature fields,
          or the underlying remote file.
        </p>
      </div>

      {/* Scan controls */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Scan window</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Start date (YYYYMMDD)</label>
            <Input
              data-testid="purge-start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">End date (YYYYMMDD)</label>
            <Input
              data-testid="purge-end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-40"
            />
          </div>
          <Button
            data-testid="purge-scan-btn"
            onClick={runScan}
            disabled={scanning}
            className="bg-slate-800 hover:bg-slate-900 text-white"
          >
            {scanning
              ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning…</>
              : <><Search className="w-4 h-4 mr-2" />Run scan</>}
          </Button>
        </CardContent>
      </Card>

      {/* Empty state */}
      {report && report.totals.total_suspects === 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6 flex items-center gap-3 text-green-700">
            <CheckCircle2 className="w-6 h-6" />
            <div>
              <p className="font-semibold">All clean.</p>
              <p className="text-sm">
                Scanned {report.totals.forms_examined} PreCons —
                no signature-shaped entries found in any{' '}
                <code>scouting_photos[]</code>.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results: one card per affected form */}
      {report && (report.forms || []).map((f) => (
        <Card key={f.form_id} className="border-amber-200" data-testid={`purge-form-card-${f.job_number}`}>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              <Badge className="bg-blue-100 text-blue-700">PreCon</Badge>
              <span className="font-bold">{f.job_number}</span>
              <span className="text-slate-400">·</span>
              <span className="text-sm text-slate-600">{f.project_name || f.client_name || f.site_location || '—'}</span>
              <span className="ml-auto flex items-center gap-2">
                <Badge variant="outline" className="border-amber-400 text-amber-700">
                  {f.suspect_count} / {f.total_photos} flagged
                </Badge>
                <Badge variant="outline" className="text-slate-600">{f.status || 'draft'}</Badge>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Reference: the form's actual signature(s) */}
            {(f.submitter_signature_url || f.reviewer_signature_url) && (
              <div className="bg-slate-50 rounded p-3 border border-slate-200">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Form signatures (kept — not touched)
                </p>
                <div className="flex gap-4 flex-wrap">
                  {f.submitter_signature_url && (
                    <div className="text-center">
                      <SafeImage
                        src={f.submitter_signature_url}
                        alt="Submitter signature"
                        className="w-32 h-20 object-contain rounded border border-slate-300 bg-white"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">Submitter: {f.submitter_name || '—'}</p>
                    </div>
                  )}
                  {f.reviewer_signature_url && (
                    <div className="text-center">
                      <SafeImage
                        src={f.reviewer_signature_url}
                        alt="Reviewer signature"
                        className="w-32 h-20 object-contain rounded border border-green-300 bg-white"
                      />
                      <p className="text-[10px] text-green-700 mt-1">Reviewer: {f.reviewer_name || '—'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* All scouting_photos entries — suspects pre-checked */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {f.photos.map((p) => {
                const k = `${f.form_id}:${p.photo_id}`;
                const checked = !!selected[k];
                const sig = p.looks_like_signature;
                return (
                  <label
                    key={p.photo_id}
                    className={`block rounded border-2 p-2 cursor-pointer transition
                      ${sig ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}
                      ${checked ? 'ring-2 ring-amber-500' : ''}`}
                    data-testid={`purge-photo-tile-${p.photo_id}`}
                  >
                    <div className="relative">
                      {p.url ? (
                        <SafeImage
                          src={p.url}
                          alt={p.caption || 'scouting photo'}
                          className="w-full h-32 object-cover rounded border border-slate-300"
                        />
                      ) : (
                        <div className="w-full h-32 flex items-center justify-center bg-slate-100 text-slate-400 border border-dashed border-slate-300 rounded">
                          <ImageOff className="w-6 h-6" />
                        </div>
                      )}
                      {sig && (
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white">
                          SIGNATURE?
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(f.form_id, p.photo_id)}
                        data-testid={`purge-checkbox-${p.photo_id}`}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-semibold ${sig ? 'text-red-700' : 'text-slate-700'}`}>
                          {sig ? 'Remove this entry' : 'Keep'}
                        </p>
                        {sig && (
                          <p className="text-[10px] text-red-600 break-all">
                            {HEURISTIC_LABEL[p.matched_heuristic] || p.matched_heuristic}
                          </p>
                        )}
                        <p className="text-[10px] text-slate-400 truncate" title={p.photo_id}>
                          id: {(p.photo_id || '').slice(0, 8)}…
                        </p>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Confirm + execute footer */}
      {report && report.totals.total_suspects > 0 && (
        <Card className="border-red-300 bg-red-50 sticky bottom-4 shadow-lg" data-testid="purge-confirm-bar">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-red-800">
                  {selectionList.length} entr{selectionList.length === 1 ? 'y' : 'ies'} selected
                  for removal across{' '}
                  {new Set(selectionList.map((s) => s.form_id)).size} form(s).
                </p>
                <p className="text-sm text-red-700 mt-1">
                  Approval status will NOT change. The Cloudinary signature
                  files will NOT be deleted. A row will be written to{' '}
                  <code>db.photo_deletion_log</code> for each removal.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-red-700 mb-1">
                  Type <code className="px-1 bg-white rounded font-bold">{CONFIRM_WORD}</code> to enable the remove button
                </label>
                <Input
                  data-testid="purge-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="PURGE"
                />
              </div>
              <Button
                data-testid="purge-execute-btn"
                onClick={executePurge}
                disabled={!canExecute}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-slate-300"
              >
                {executing
                  ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Executing…</>
                  : <><Camera className="w-4 h-4 mr-2" />Remove {selectionList.length} selected</>}
              </Button>
            </div>

            <label
              className="flex items-start gap-2 text-sm text-red-800 cursor-pointer pt-2 border-t border-red-200"
              data-testid="purge-also-delete-remote-label"
            >
              <input
                type="checkbox"
                checked={alsoDeleteRemote}
                onChange={(e) => setAlsoDeleteRemote(e.target.checked)}
                data-testid="purge-also-delete-remote-checkbox"
                className="mt-1"
              />
              <span>
                <b>Also delete files from OneDrive</b> (recommended — keeps the form&apos;s
                own signature file untouched; skips entries whose URL matches the
                form&apos;s signature; logs{' '}
                <code className="px-1 bg-white rounded">onedrive_deleted: true/false</code>{' '}
                per item).
              </span>
            </label>
          </CardContent>
        </Card>
      )}

      {/* Last execute result */}
      {executeResult && (
        <Card className="border-slate-300" data-testid="purge-execute-result">
          <CardHeader>
            <CardTitle className="text-base">Last purge run · {executeResult.run_id}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 text-sm mb-3">
              <span>Requested: <b>{executeResult.totals.requested}</b></span>
              <span className="text-green-700">Removed: <b>{executeResult.totals.removed}</b></span>
              <span className="text-amber-700">Skipped: <b>{executeResult.totals.skipped}</b></span>
            </div>
            <div className="max-h-64 overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="text-left p-2">form_id</th>
                    <th className="text-left p-2">photo_id</th>
                    <th className="text-left p-2">DB</th>
                    <th className="text-left p-2">OneDrive</th>
                    <th className="text-left p-2">heuristic / detail</th>
                  </tr>
                </thead>
                <tbody>
                  {executeResult.results.map((r, i) => (
                    <tr key={i} className="border-t border-slate-200">
                      <td className="p-2 font-mono">{(r.form_id || '').slice(0, 8)}…</td>
                      <td className="p-2 font-mono">{(r.photo_id || '').slice(0, 8)}…</td>
                      <td className={`p-2 font-semibold ${
                        r.status === 'removed' ? 'text-green-700' : 'text-amber-700'
                      }`}>
                        {r.status}
                      </td>
                      <td className="p-2">
                        {r.status === 'removed' && (
                          r.onedrive_deleted
                            ? <span className="text-green-700">✓ deleted</span>
                            : <span
                                className="text-slate-500"
                                title={r.remote_skip_reason || ''}
                              >
                                — kept ({r.remote_skip_reason || 'n/a'})
                              </span>
                        )}
                      </td>
                      <td className="p-2 text-slate-600">{r.matched_heuristic || r.detail || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
